import type { FilterFn, Row } from "@tanstack/react-table";

import { createColumnHelper } from "@tanstack/react-table";

import type { TradingClientRow, TradingColumnLabels } from "../../types";

import { formatCurrencyWithSymbol } from "../../../utils/currency";
import { BodyTypo } from "../../Common/BodyTypo";
import { HeaderTypo } from "../../Common/HeaderTypo";
import { formatNumberV2 } from "../../utils";
import { createBackofficeFieldDecorators } from "../../utils/redactedFieldsHelper";
import { RebateProgramCell } from "./RebateProgramCell";
const columnHelper = createColumnHelper<TradingClientRow>();

export const rebateAccountFilter: FilterFn<TradingClientRow> = (
  row,
  columnId,
  value,
) => {
  if (!value) return true;

  if (columnId !== "rebateAccountID") return true;

  const rebateAccountID = row.original.rebateAccountID;
  const selectedIDs: string[] = value;

  return selectedIDs.includes(`${rebateAccountID}`);
};

export const sortByName = (
  rowA: Row<TradingClientRow>,
  rowB: Row<TradingClientRow>,
) => {
  const rowAName: string = rowA.original.fullName;
  const rowBName: string = rowB.original.fullName;
  return rowAName.localeCompare(rowBName);
};

export const clientTradingColumns = ({
  translatedContent,
  language,
  redactedFields = [],
  isBackoffice = false,
  showOfferIdColumn = false,
  onSharedProgramClick,
}: {
  translatedContent?: TradingColumnLabels;
  language: string;
  redactedFields?: string[];
  isBackoffice?: boolean;
  showOfferIdColumn?: boolean;
  onSharedProgramClick?: (hasOfferID: string) => void;
}) => {
  const { hiddenColumns, decorateTitle, getRedactedClassName } =
    createBackofficeFieldDecorators(redactedFields, isBackoffice);

  const columns = [
    columnHelper.accessor("tradingAccountID", {
      header: () => <HeaderTypo text={translatedContent?.acc} />,
      cell: (info) => (
        <>
          <p>{info.getValue()}</p>
          <p className="text-sm text-text-secondary">
            {info?.row?.original?.tradingAccountType}
          </p>
        </>
      ),
      sortingFn: "alphanumeric",
      sortDescFirst: false,
      enableGlobalFilter: false,
      size: 105,
    }),
    columnHelper.accessor("fullName", {
      header: () => (
        <HeaderTypo
          text={decorateTitle(
            "fullName",
            !hiddenColumns.includes("name")
              ? translatedContent?.name
              : translatedContent?.emailAddress,
          )}
        />
      ),
      cell: (info) => (
        <>
          {!hiddenColumns.includes("name") ? (
            <p className="text-text-primary">{info?.row?.original?.fullName}</p>
          ) : null}
          {!hiddenColumns.includes("email") ? (
            <p className="text-sm text-text-secondary">
              {info?.row?.original?.email}
            </p>
          ) : null}
        </>
      ),
      sortingFn: sortByName,
      enableGlobalFilter: false,
      size: 165,
      meta: { ...getRedactedClassName("fullName") },
    }),
    columnHelper.accessor("clientId", {
      header: () => (
        <HeaderTypo
          text={decorateTitle("clientId", translatedContent?.clientId)}
        />
      ),
      cell: (info) => <p>{info?.row?.original?.clientId}</p>,
      sortingFn: "alphanumeric",
      enableGlobalFilter: false,
      size: 165,
      meta: { ...getRedactedClassName("clientId") },
    }),
    columnHelper.accessor("totalVolumeUSD", {
      header: () => <HeaderTypo text={translatedContent?.totalVolume} />,
      cell: (info) => {
        const value = info.getValue();
        return (
          <BodyTypo
            text={formatCurrencyWithSymbol(
              value ?? 0,
              "USD",
              language,
              false,
              false,
            )}
          />
        );
      },
      enableGlobalFilter: false,
      sortingFn: "alphanumeric",
      size: 140,
    }),
    columnHelper.accessor("volumeFX", {
      header: () => <HeaderTypo text={translatedContent?.colVolFx} />,
      cell: (info) => (
        <BodyTypo text={formatNumberV2(info.getValue(), language)} />
      ),
      enableGlobalFilter: false,
      size: 90,
    }),
    columnHelper.accessor("volumeXAU", {
      header: () => <HeaderTypo text={translatedContent?.colVolXau} />,
      cell: (info) => (
        <BodyTypo text={formatNumberV2(info.getValue(), language)} />
      ),
      enableGlobalFilter: false,
      size: 100,
    }),
    columnHelper.accessor("volumeXAG", {
      header: () => <HeaderTypo text={translatedContent?.colVolXag} />,
      cell: (info) => (
        <BodyTypo text={formatNumberV2(info.getValue(), language)} />
      ),
      enableGlobalFilter: false,
      size: 100,
    }),
    columnHelper.accessor("volumeOil", {
      header: () => <HeaderTypo text={translatedContent?.colVolOil} />,
      cell: (info) => (
        <BodyTypo text={formatNumberV2(info.getValue(), language)} />
      ),
      enableGlobalFilter: false,
      size: 90,
    }),
    columnHelper.accessor("volumeCFD", {
      header: () => <HeaderTypo text={translatedContent?.colVolCfd} />,
      cell: (info) => (
        <BodyTypo text={formatNumberV2(info.getValue(), language)} />
      ),
      enableGlobalFilter: false,
      size: 100,
    }),
    columnHelper.accessor("totalEarnings", {
      header: () => <HeaderTypo text={translatedContent?.totalEarnings} />,
      cell: (info) => {
        const currency = info?.row?.original?.currency;
        return (
          <BodyTypo
            text={formatCurrencyWithSymbol(
              info.getValue(),
              currency,
              language,
              false,
              false,
            )}
          />
        );
      },
      enableGlobalFilter: false,
      size: 130,
    }),
    columnHelper.accessor("hasOfferID", {
      header: () => <HeaderTypo text={translatedContent?.hasOfferID} />,
      cell: (info) => (
        <RebateProgramCell
          hasOfferID={info.getValue()}
          subIBs={info.row.original.subIBs}
          onSharedProgramClick={onSharedProgramClick}
        />
      ),
      enableGlobalFilter: false,
      sortingFn: "alphanumeric",
      size: 140,
    }),
    columnHelper.accessor("rebateAccountID", {
      header: () => <HeaderTypo text={translatedContent?.rebateAccount} />,
      cell: (info) => <BodyTypo text={info.getValue()} />,
      enableGlobalFilter: false,
      filterFn: rebateAccountFilter,
      enableColumnFilter: true,
      sortingFn: "alphanumeric",
      sortDescFirst: false,
      size: 140,
    }),
  ];

  return columns.filter((column) => {
    const columnKey = String(column.accessorKey).toLowerCase();

    if (columnKey === "hasofferid" && !showOfferIdColumn) {
      return false;
    }

    return !hiddenColumns.some((k) => k.toLowerCase() === columnKey);
  });
};
